// Copyright (c) 2025. CYBERKOALA LLC.
//
// SPDX-License-Identifier: Proprietary License.
//
// Redistribution and use in source and binary forms, with or without
// modification, are restricted.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { StatusContext } from '@/context/status-context/status-context.tsx';
import { useAuth } from '@/context/auth-context/use-auth.ts';
import { useSocket } from '@/context/socket-context/use-external-socket.ts';
import { API_BASE } from '@/config.ts';
import { useUserStatusStore } from '@/stores/use-status-store.ts';
import { isPardusBrowser } from '@/components/check-user-agent.tsx';

const IDLE_TIMEOUT = 10 * 60 * 1_000; // 10min
const CHECK_INTERVAL = 1 * 10 * 1_000; // 10 sec
const DEBOUNCE_TO_AWAY = 2_000; // debounce before switching to away
const DEBOUNCE_TO_ONLINE = 1_000; // debounce before switching to online

const AUDIO_LEVEL_THRESHOLD = 0.03;

/**
 * Обернуть внутри CallProvider или ExternalSocket (чтобы этот провайдер был внутри), затем получать
 * mic volume, is speaking? state. Если говорит раз в течение 5 минут -не включать АФК, сбрасывать таймер
 * @param children
 * @constructor
 */
export const UserStatusProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const {
    status,
    setStatus,
    description,
    setDescription,
    updatedAt,
    setUpdatedAt,
  } = useUserStatusStore();
  const [hasInitialized, setHasInitialized] = useState(false);
  const { user } = useAuth();
  const socket = useSocket();
  const didSetInitialOnlineRef = useRef(false);
  const lastSentRef = useRef<{ status: string; description: string } | null>(null);
  const lastActivityRef = useRef<number>(Date.now());
  const awayTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onlineTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * Reset activity (used for both mouse + speaking)
   * @experimental
   */
  const resetActivity = useCallback(() => {
    lastActivityRef.current = Date.now();

    if (status === "away") {
      if (awayTimerRef.current) {
        clearTimeout(awayTimerRef.current);
        awayTimerRef.current = null;
      }

      if (onlineTimerRef.current) clearTimeout(onlineTimerRef.current);
      onlineTimerRef.current = setTimeout(() => {
        setStatus("online");
        onlineTimerRef.current = null;
      }, DEBOUNCE_TO_ONLINE);
    }
  }, [status, setStatus]);

  // /**
  //  * Watch user inactivity  (mousemove etc.)
  //  * It will not work if during app work enable Guardian (change network)
  //  * TO test: open multiple tabs, switch on/off VPN and see status blinking
  //  * @experimental
  //  */
  useEffect(() => {
    if (isPardusBrowser()) return; // only web for testing
    const handleMouse = resetActivity;

    // TODO: what to do if active tab and active app, this is switches always
    window.addEventListener('mousemove', handleMouse); // WARN: ELECTRON DOESN'T HAVE NORMAL HANDLING OF THIS!

    const interval = setInterval(() => {
      const inactiveFor = Date.now() - lastActivityRef.current;

      if (inactiveFor > IDLE_TIMEOUT && status !== 'away') {
        if (onlineTimerRef.current) {
          clearTimeout(onlineTimerRef.current);
          onlineTimerRef.current = null;
        }

        if (!awayTimerRef.current) {
          awayTimerRef.current = setTimeout(() => {
            setStatus('away');
            awayTimerRef.current = null;
          }, DEBOUNCE_TO_AWAY);
        }
      }
    }, CHECK_INTERVAL);

    return () => {
      window.removeEventListener('mousemove', resetActivity);
      clearInterval(interval);
      if (awayTimerRef.current) clearTimeout(awayTimerRef.current);
      if (onlineTimerRef.current) clearTimeout(onlineTimerRef.current);
    };
  }, [status, resetActivity]);


  useEffect(() => {
    if (!user) return;

    const fetchUserSettings = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/user-settings`, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (response.status == 200) {
          const data = await response.json();

          setDescription(data.description);
          setUpdatedAt(new Date(data.updatedAt || Date.now()));

          /**
           * @experimental
           */
          /**
           * ВАЖНО:
           * если сервер вернул offline / null / invisible —
           * считаем это "новый вход" и переводим в online ОДИН РАЗ
           */
          if (!didSetInitialOnlineRef.current && (!data.status || data.status === 'offline')) {
            didSetInitialOnlineRef.current = true;
            setStatus('online');
          } else {
            setStatus(data.status);
          }
          setHasInitialized(true);
        }
      } catch (error) {
        console.error(error);
      }
    };

    fetchUserSettings();
  }, [user]);

  /**
   * Initial load. Save updates to server + broadcast presence
   */
  useEffect(() => {
    if (!hasInitialized || !user) return;

    const payload = { status, description };

    /**
     * @experimental
     */
    if (
      lastSentRef.current &&
      lastSentRef.current.status === status &&
      lastSentRef.current.description === description
    ) {
      return;
    }
    /**
     * @experimental
     */
    lastSentRef.current = payload;

    const saveUserSettings = async () => {
      try {
        await fetch(`${API_BASE}/api/user-settings`, {
          method: 'POST',
          body: JSON.stringify(payload),
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (!socket) return;
        // sends back to self status update to Update UI, members sidebar
        if (socket?.readyState !== WebSocket.OPEN) {
          return;
        }
        socket.send(
          JSON.stringify({
            type: 'presence-update', // make sure that its INCOMING update (not outgoing update-presence). UI Listens this
            users: [
              {
                userId: user.id,
                status: status,
                description: description,
                devices: [],
              },
            ],
          })
        );
      } catch (error) {
        console.error(error);
      }
    };

    saveUserSettings();
  }, [status, description, hasInitialized]);

  /**
   * Handle incoming presence / channel-state updates
   */
  useEffect(() => {
    if (!socket) return;

    const onMessage = (event: MessageEvent) => {
      const msg = JSON.parse(event.data);
      if (msg.type === 'presence-update') {
        const u = msg.users.find((u: any) => u.userId === user?.id);
        if (!u) return;

        const incomingTime = new Date(u.updatedAt || Date.now());
        if (incomingTime <= updatedAt) return;

        setStatus(u.status);
        setDescription(u.description || '');
        setUpdatedAt(incomingTime);
      }

      if (msg.type === "channel-state-update") {
        const states = msg.states || [];
        const myStates = states.filter(
          (s: any) => s.participantIdentity === user?.name // TODO: need ID
        );

        if (myStates.length === 0) return;

        // const anyDeviceSpeaking = myStates.some((s: any) =>
        //   s.devices?.some((d: any) => d.audioLevel > AUDIO_LEVEL_THRESHOLD) // threshold to set online
        // );

        /**
         * @experimental
         */
        const anyDeviceSpeaking = myStates.some(
          (s: any) => s.devices?.some((d: any) => d.speaking == true)
        );

        // TODO: after testing, uncomment this
        if (anyDeviceSpeaking) {
          // resetActivity();
        }
      }

    };

    socket.addEventListener('message', onMessage);
    return () => socket.removeEventListener('message', onMessage);
  }, [socket, user?.id, updatedAt]);

  return (
    <StatusContext.Provider
      value={{
        status,
        setStatus,
        description,
        setDescription,
        updatedAt,
        setUpdatedAt,
      }}
    >
      {children}
    </StatusContext.Provider>
  );
};
